/**
 * MODULE: _shared/lib/frontend/api/sessionAuthReady.ts
 * PURPOSE: Synchronous and reactive gates for API calls before a usable JWT exists
 * PUBLIC_API: readApiAuthReady, useSessionAuthReady, BRAZN_SESSION_UPDATED_EVENT
 * AGENT_SAFETY: SAFE
 * RECENT: 2026-06-04 [2.2.1272] — readApiAuthReady + useSessionAuthReady gate dashboard/CRM fetches without AuthProvider (HMR-safe).
 */

import { useSyncExternalStore } from 'react';
import {
  BRAZN_SESSION_UPDATED_EVENT,
  isBraznRefreshCooldownActive,
} from './authTokenRefresh';
import { getAuthToken, isAccessTokenExpired } from './config';

export { BRAZN_SESSION_UPDATED_EVENT };

/** True when a non-expired access token is present and refresh is not in failure cooldown. */
export function readApiAuthReady(): boolean {
  const token = getAuthToken();
  if (!token) return false;
  if (isAccessTokenExpired(token) && isBraznRefreshCooldownActive()) return false;
  return !isAccessTokenExpired(token);
}

function subscribeSessionStore(onStoreChange: () => void): () => void {
  const handler = () => onStoreChange();
  window.addEventListener('storage', handler);
  window.addEventListener(BRAZN_SESSION_UPDATED_EVENT, handler);
  return () => {
    window.removeEventListener('storage', handler);
    window.removeEventListener(BRAZN_SESSION_UPDATED_EVENT, handler);
  };
}

/** Reactive gate for TanStack Query `enabled` and mount effects (no AuthProvider). */
export function useSessionAuthReady(): boolean {
  return useSyncExternalStore(subscribeSessionStore, readApiAuthReady, () => false);
}
